import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import axios from "axios";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { post } from "~/Services";
import { passengerHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import {
  renderActionsColumn,
  renderDateColumn,
  renderPublishedColumn,
} from "~/Utils/TableRenderFunction";
import { useHelpHeadingDocumentDetails } from "./PageHeader.Hooks";

export const PageHeader = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [pageSize, setPageSize] = useState<number>(10);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  const publishedData: TypeData[] = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
  ];

  const { data: drilldown, refetch: refetchDocument } =
    useHelpHeadingDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    if (query.get("subActiveTab") === "parentAirport") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("subActiveTab")]);

  // ✅ Fixed Search + Publish Filters
  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    // --- SEARCH FILTER ---
    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter((item: any) => {
        const title = String(item?.field_page_title_en || "").toLowerCase();
        const headerName = String(item?.field_page_header_name || "").toLowerCase();
        return title.includes(searchLower) || headerName.includes(searchLower);
      });
    }

    // --- PUBLISHED FILTER ---
if (selectedPublished !== "") {
  filteredData = filteredData.filter((item: any) => {
    const publishedVal = String(item?.published).toLowerCase();
    const selectedVal = String(selectedPublished).toLowerCase();

    // Match true/false values directly
    return publishedVal === selectedVal;
  });
}

   

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `${passengerHost}/api/page-headers/all-fields`;
    axios
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          const envelopes = (res.data || []).map((item: any) => ({
            ...item,
            published: String(item?.published).toLowerCase(),
          }));
          setTotalRecords(envelopes.length);
          setData(envelopes);
          console.log(envelopes, "testing page header");
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };

  const columns: TableColumnsType = [
    {
      title: "Banner Image",
      dataIndex: "field_page_banner_image",
      key: "field_page_banner_image",
      render: (img) =>
        img ? (
          <img
            src={img}
            alt="banner"
            style={{
              width: 80,
              height: 50,
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Card Name",
      dataIndex: "field_page_title_en",
      key: "field_page_title_en",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_page_title_en"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_page_title_en"
          ? "descend"
          : undefined,
    },
    {
      title: "Airport",
      dataIndex: "field_page_header_airport",
      key: "field_page_header_airport",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_page_header_airport"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_page_header_airport"
          ? "descend"
          : undefined,
    },
    {
      title: "Header Name",
      dataIndex: "field_page_header_name",
      key: "field_page_header_name",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_page_header_name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_page_header_name"
          ? "descend"
          : undefined,
    },
    {
      title: "Display Path",
      dataIndex: "field_page_header_display_path",
      key: "field_page_header_display_path",
      render: (text) => decodeHTML(text),
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
          ? "descend"
          : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
          ? "descend"
          : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      render: (value: any, record: any) => {
        const created = record?.created
          ? moment(record.created).format("YYYY-MM-DD HH:mm:ss")
          : null;
        const updated = value
          ? moment(value).format("YYYY-MM-DD HH:mm:ss")
          : null;

        if (created && updated && created === updated) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
          ? "descend"
          : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(
              `/dedicated/passenger/pageheader/pageheader/add?editId=${record.id}`
            );
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
      order = "asc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...data];
    setSortedData(
      tempData.sort((a, b) => handleSort(a, b, order, column.dataIndex))
    );
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action,
      method,
      module: "Page Header",
      subModule: "PageHeader",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [{ appName: "PASSENGER_ECMS", apiUrl }],
      payload: data,
      status,
    };

    post("/management/publish", payload)
      .then(() => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        navigate("/dedicated/status-information?activeTab=pending");
      })
      .catch(() => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteBrandRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${passengerHost}/api/page-headers-crud-data/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch {}
  }

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) =>
            col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {},
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData || []}
        isLoading={isLoading}
        exportFileName="OUTLET"
        isPagination={true}
        recordsPerPage={5}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search"}
        onClick={(record) => setSelectedRowData(record)}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />

      <div className="pagination flex justify-end mt-4">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={<Typography.Title level={4} className={"!mb-0"}>Detailed View</Typography.Title>}
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "FlightInfoHeaders") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteBrandRequest}
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (
    ["field_location_airports", "field_location_level_name"].includes(sortBy)
  ) {
    const titleA = a[sortBy]?.toLowerCase() || "";
    const titleB = b[sortBy]?.toLowerCase() || "";

    if (order === "desc") {
      return titleA < titleB ? 1 : -1;
    } else if (order === "asc") {
      return titleA > titleB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
